import {
  catchError,
  concatMap,
  switchMap,
  filter,
  map,
  tap,
  withLatestFrom,
  take
} from 'rxjs/operators'
import { authActions } from 'src/app/core/auth/auth.actions'
import { AppState } from 'src/app/core/core.state'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import {
  noOperationAction,
  ResourceEffects
} from 'src/app/shared/resource-effects'
import { forkJoin, of } from 'rxjs'

import { Injectable } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { CartItem } from './cart-item'
import { cartItemActions } from './cart-item.actions'
import * as fromCartItem from './cart-item.reducer'
import { cartItemSelectors } from './cart-item.selectors'
import { CartItemService } from './cart-item.service'
import { productActions } from '../../product/product.actions'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { LocalStorageKeys } from 'src/app/core/local-storage.keys'
import { LocalStorageService } from 'src/app/core/local-storage.service'
import { NotificationService } from 'src/app/core/notification.service'

type PartialCartItemUpsertResult =
  | {
      success: true
      sourceCartItem: Partial<CartItem>
      createdCartItem: CartItem
    }
  | {
      success: false
      sourceCartItem: Partial<CartItem>
      error: unknown
    }

type SuccessfulPartialCartItemUpsert = Extract<
  PartialCartItemUpsertResult,
  { success: true }
>
type FailedPartialCartItemUpsert = Extract<
  PartialCartItemUpsertResult,
  { success: false }
>

@Injectable()
export class CartItemEffects extends ResourceEffects<CartItem> {
  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
    public cartItemService: CartItemService
  ) {
    super(fromCartItem.FEATURE_NAME, cartItemService, cartItemActions, actions$)
  }

  emptyCart = createEffect(() =>
    this.actions$.pipe(
      ofType(cartItemActions.emptyCart),
      withLatestFrom(this.store.select(cartItemSelectors.selectAll)),
      concatMap(([action, cartItems]) => {
        return cartItems.map((cartItem) =>
          cartItemActions.deleteOne({
            id: cartItem.id
          })
        )
      }),
      tap((x) => this.store.dispatch(cartItemActions.resetState()))
    )
  )

  resetState = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      map(() => cartItemActions.resetState())
    )
  )

  onFetchOneByIdAnReset = createEffect(() =>
    this.actions$.pipe(
      ofType(cartItemActions.fetchOneByIdAndReset),
      withLatestFrom(this.store.select(authSelectors.selectMember)),
      map(([action, member]) => {
        this.cartItemService
          .getMany(
            RequestQueryBuilder.create({
              search: { id: action.id, memberId: member.id },
              join: [
                { field: 'product' },
                { field: 'product.linkedSubscriptions' },
                { field: 'product.translations' },
                { field: 'subscription' },
                { field: 'subscription.translations' },
                { field: 'course' },
                { field: 'course.translations' },
                { field: 'course.product' }
                // { field: 'course.product.linkedSubscriptions' }
              ]
            })
          )
          .pipe(take(1))
          .subscribe((cartItems) => {
            cartItems.data.map((item) => {
              this.cartItemService.deleteOne(item.id)
            })
          })
        return cartItemActions.resetState()
      })
    )
  )

  onLoadMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember),
      withLatestFrom(this.store.select(authSelectors.selectMember)),
      switchMap(([action, member]) =>
        this.cartItemService
          .getMany(
            RequestQueryBuilder.create({
              search: { memberId: member.id },
              join: [
                { field: 'product' },
                //{ field: 'product.linkedSubscriptions' },
                { field: 'product.translations' },
                { field: 'subscription' },
                { field: 'subscription.translations' },
                { field: 'course' },
                { field: 'course.translations' },
                { field: 'course.product' }
                //{ field: 'course.product.linkedSubscriptions' }
              ]
            })
          )
          .pipe(
            map((cartItems) => {
              cartItems?.data?.map((carItem) => {
                if (carItem.productId) {
                  this.store.dispatch(
                    productActions.fetchMany({
                      params: RequestQueryBuilder.create({
                        search: {
                          id: carItem.productId
                        },
                        join: [
                          { field: 'linkedSubscriptions' },
                          { field: 'translations' }
                        ]
                      })
                    })
                  )
                }
                if (
                  carItem.params?.bookableLinkedProductId &&
                  carItem.params?.bookableLinkedProductId != carItem.productId
                ) {
                  this.store.dispatch(
                    productActions.fetchMany({
                      params: RequestQueryBuilder.create({
                        search: {
                          id: carItem.params?.bookableLinkedProductId
                        },
                        join: [
                          { field: 'linkedSubscriptions' },
                          { field: 'translations' }
                        ]
                      })
                    })
                  )
                }
              })
              return normalizationActions.upsertMany({
                entities: normalizeToSchema({ cartItems: cartItems.data })
              })
            })
          )
      )
    )
  )

  insertFromPartialOnMemberLoad = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember),
      withLatestFrom(this.store.select(cartItemSelectors.selectState)),
      filter(
        ([action, cartItemState]) =>
          Boolean(action.member) && Boolean(cartItemState)
      ),
      concatMap(([action, cartItemState]) => {
        const storedPartialCartItems = window.localStorage.getItem(
          LocalStorageKeys.PARTIAL_CART_ITEMS
        )
        const partialCartItemsFromStorage: Partial<CartItem>[] =
          storedPartialCartItems ? JSON.parse(storedPartialCartItems) : []
        const partialCartItemsToInsert = partialCartItemsFromStorage.length
          ? partialCartItemsFromStorage
          : cartItemState.partialCartItems

        console.log(
          'Inserting partial cartItems for authenticated member',
          partialCartItemsToInsert
        )

        if (!partialCartItemsToInsert.length) {
          return of(noOperationAction())
        }

        this.localStorageService.setItem(
          LocalStorageKeys.LOAD_PARTIAL_MEMBER,
          'true'
        )

        return forkJoin(
          partialCartItemsToInsert.map((cartItem) =>
            this.cartItemService
              .createOne({
                ...cartItem,
                params: {
                  ...cartItem.params,
                  surveyAnswers: cartItem.params?.surveyAnswers?.map(
                    (surveyAnswer) => ({
                      ...surveyAnswer,
                      memberId: action.member.id
                    })
                  )
                },
                memberId: action.member.id
              })
              .pipe(
                map(
                  (createdCartItem): PartialCartItemUpsertResult => ({
                    success: true,
                    sourceCartItem: cartItem,
                    createdCartItem
                  })
                ),
                catchError((error) =>
                  of({
                    success: false,
                    sourceCartItem: cartItem,
                    error
                  } as PartialCartItemUpsertResult)
                )
              )
          )
        ).pipe(
          map((results) => {
            const createdCartItems = results
              .filter(
                (result): result is SuccessfulPartialCartItemUpsert =>
                  result.success
              )
              .map((result) => result.createdCartItem)
            const failedResults = results.filter(
              (result): result is FailedPartialCartItemUpsert => !result.success
            )

            if (failedResults.length) {
              return cartItemActions.cleanupFailedPartialCartItemUpserts({
                attemptedCartItems: partialCartItemsToInsert,
                createdCartItems,
                failedCartItems: failedResults.map(
                  (result) => result.sourceCartItem
                ),
                error: failedResults[0].error
              })
            }

            this.store.dispatch(cartItemActions.resetPartials())

            return normalizationActions.upsertMany({
              entities: normalizeToSchema({
                [fromCartItem.FEATURE_NAME]: createdCartItems
              })
            })
          })
        )
      })
    )
  )

  cleanupFailedPartialCartItemUpserts = createEffect(() =>
    this.actions$.pipe(
      ofType(cartItemActions.cleanupFailedPartialCartItemUpserts),
      concatMap((action) => {
        const createdCartItemIds = action.createdCartItems.map(
          (cartItem) => cartItem.id
        )

        const cleanupRequests = createdCartItemIds.length
          ? forkJoin(
              createdCartItemIds.map((id) =>
                this.cartItemService
                  .deleteOne(id)
                  .pipe(catchError(() => of(null)))
              )
            )
          : of([])

        return cleanupRequests.pipe(
          tap(() => {
            this.store.dispatch(cartItemActions.resetPartials())
            this.notificationService.emit(
              'alerts.failedToCreateCartItems',
              'error'
            )
          }),
          map(() => {
            if (!createdCartItemIds.length) {
              return noOperationAction()
            }

            return cartItemActions.removeMany({
              ids: createdCartItemIds
            })
          })
        )
      })
    )
  )
  emptyCartUnauthenticated = createEffect(() =>
    this.actions$.pipe(
      ofType(cartItemActions.emptyCartUnauthenticated),
      withLatestFrom(this.store.select(cartItemSelectors.selectPartials)),
      concatMap(([action, cartItems]) => {
        this.localStorageService.removeItem(
          LocalStorageKeys.UN_AUTHENTICATED_CART_ITEMS
        )
        window.localStorage.removeItem(LocalStorageKeys.PARTIAL_CART_ITEMS)
        return cartItems.map((cartItem) =>
          cartItemActions.deleteOneCartItemUnauthenticated({
            id: cartItem.id
          })
        )
      }),
      tap((x) => this.store.dispatch(cartItemActions.resetState()))
    )
  )
}
